import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n";
import { isoToEu } from "@/lib/dates";
import { DeadlineDialog, TaskDialog } from "@/components/forms";
import CalendarDialog from "@/components/CalendarDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Plus, CalendarPlus, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const MONTHS = {
  en: ["January", "February", "March", "April", "May", "June", "July", "August",
       "September", "October", "November", "December"],
  lt: ["Sausis", "Vasaris", "Kovas", "Balandis", "Gegužė", "Birželis", "Liepa",
       "Rugpjūtis", "Rugsėjis", "Spalis", "Lapkritis", "Gruodis"],
};
const WEEKDAYS = {
  en: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
  lt: ["Pr", "An", "Tr", "Kt", "Pn", "Št", "Sk"],
};

const pad2 = (n) => String(n).padStart(2, "0");
const isoOf = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const startOfWeek = (d) => {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
};

function eventCls(ev) {
  if (ev.kind === "task") return "bg-emerald-50 border-emerald-300 text-emerald-800";
  if (ev.deadline_type === "Court hearing") return "bg-red-50 border-red-300 text-red-800";
  if (ev.deadline_type === "Procedural deadline") return "bg-amber-50 border-amber-300 text-amber-800";
  return "bg-sky-50 border-sky-300 text-sky-800";
}

const LEGEND = [
  ["bg-red-400", "val.Court hearing"],
  ["bg-amber-400", "val.Procedural deadline"],
  ["bg-sky-400", "calendar.kind_deadline"],
  ["bg-emerald-400", "calendar.kind_task"],
];

function EventChip({ ev, onSelect }) {
  return (
    <button
      data-testid={`cal-event-${ev.kind}-${ev.id}`}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(ev);
      }}
      className={`block w-full text-left truncate rounded border px-1.5 py-0.5 text-[11px] font-medium transition-opacity hover:opacity-75 ${eventCls(ev)} ${ev.status === "Completed" ? "opacity-50 line-through" : ""}`}
    >
      {ev.start_time ? `${ev.start_time} ` : ""}{ev.description}
    </button>
  );
}

export default function Calendar() {
  const { t, lang } = useT();
  const navigate = useNavigate();
  const L = lang === "lt" ? "lt" : "en";
  const [cursor, setCursor] = useState(() => new Date());
  const [view, setView] = useState("month");
  const [events, setEvents] = useState([]);
  const [matters, setMatters] = useState([]);
  const [selected, setSelected] = useState(null);
  const [chooser, setChooser] = useState(null);
  const [createMatter, setCreateMatter] = useState("");
  const [deadlineInit, setDeadlineInit] = useState(null);
  const [taskInit, setTaskInit] = useState(null);
  const [calEvent, setCalEvent] = useState(null);

  const todayIsoStr = isoOf(new Date());

  useEffect(() => {
    api.get("/matters")
      .then((r) => setMatters(r.data.filter((m) => m.status !== "Closed" && m.status !== "Archived")))
      .catch(() => {});
  }, []);

  const range = useMemo(() => {
    if (view === "month") {
      const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
      const start = startOfWeek(first);
      const end = new Date(start);
      end.setDate(end.getDate() + 41);
      return [start, end];
    }
    if (view === "week") {
      const start = startOfWeek(cursor);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return [start, end];
    }
    return [cursor, cursor];
  }, [cursor, view]);
  const rangeKey = `${isoOf(range[0])}_${isoOf(range[1])}`;

  const load = () => {
    const [s, e2] = rangeKey.split("_");
    api.get("/calendar/events", { params: { start: s, end: e2 } })
      .then((r) => setEvents(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(load, [rangeKey]);

  const byDate = useMemo(() => {
    const map = {};
    for (const ev of events) (map[ev.due_date] ||= []).push(ev);
    for (const k in map) {
      map[k].sort((a, b) => ((a.start_time || "99:99") < (b.start_time || "99:99") ? -1 : 1));
    }
    return map;
  }, [events]);

  const shift = (dir) => {
    const d = new Date(cursor);
    if (view === "month") d.setMonth(d.getMonth() + dir);
    else if (view === "week") d.setDate(d.getDate() + dir * 7);
    else d.setDate(d.getDate() + dir);
    setCursor(d);
  };

  const label = view === "month"
    ? `${(MONTHS[L] || MONTHS.en)[cursor.getMonth()]} ${cursor.getFullYear()}`
    : view === "week"
      ? `${isoToEu(isoOf(range[0]))} – ${isoToEu(isoOf(range[1]))}`
      : isoToEu(isoOf(cursor));

  const openChooser = (dateIso) => setChooser({ date: dateIso, kind: "deadline", matterId: "" });

  const confirmChooser = () => {
    if (!chooser?.matterId) {
      toast.error(t("timer.select_first"));
      return;
    }
    setCreateMatter(chooser.matterId);
    if (chooser.kind === "task") {
      setTaskInit({ due_date: chooser.date });
    } else {
      setDeadlineInit({
        due_date: chooser.date,
        deadline_type: chooser.kind === "hearing" ? "Court hearing" : "Procedural deadline",
      });
    }
    setChooser(null);
  };

  const monthCells = useMemo(() => {
    if (view !== "month") return [];
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(range[0]);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [range, view]);

  const weekDays = useMemo(() => {
    if (view !== "week") return [];
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(range[0]);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [range, view]);

  return (
    <div className="max-w-7xl mx-auto lg:p-6" data-testid="calendar-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h1 className="font-serif text-3xl tracking-tight text-slate-900">{t("calendar.title")}</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" data-testid="cal-today" onClick={() => setCursor(new Date())}>
            {t("calendar.today")}
          </Button>
          <Button variant="outline" size="sm" data-testid="cal-prev" onClick={() => shift(-1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" data-testid="cal-next" onClick={() => shift(1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <div className="flex rounded-md border border-slate-200 overflow-hidden">
            {["month", "week", "day"].map((v) => (
              <button
                key={v}
                data-testid={`cal-view-${v}`}
                onClick={() => setView(v)}
                className={`px-3 py-1.5 text-xs font-semibold transition-colors ${view === v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
              >
                {t(`calendar.${v}`)}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="font-serif text-xl text-slate-800" data-testid="cal-period-label">{label}</div>
        <div className="flex items-center gap-3 text-[11px] text-slate-500" data-testid="cal-legend">
          {LEGEND.map(([dot, key]) => (
            <span key={key} className="inline-flex items-center gap-1.5">
              <span className={`h-2 w-2 rounded-full ${dot}`} /> {t(key)}
            </span>
          ))}
        </div>
      </div>

      {view === "month" && (
        <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="cal-month-grid">
          <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
            {(WEEKDAYS[L] || WEEKDAYS.en).map((w) => (
              <div key={w} className="py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                {w}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-px bg-slate-200">
            {monthCells.map((d) => {
              const dIso = isoOf(d);
              const evs = byDate[dIso] || [];
              const inMonth = d.getMonth() === cursor.getMonth();
              return (
                <div
                  key={dIso}
                  data-testid={`cal-day-${dIso}`}
                  onClick={() => openChooser(dIso)}
                  className={`bg-white min-h-16 sm:min-h-28 p-1 sm:p-1.5 cursor-pointer hover:bg-slate-50 transition-colors ${inMonth ? "" : "opacity-40"}`}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCursor(new Date(d));
                      setView("day");
                    }}
                    data-testid={`cal-daynum-${dIso}`}
                    className={`text-xs font-semibold mb-1 h-6 w-6 rounded-full inline-flex items-center justify-center transition-colors ${dIso === todayIsoStr ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-200"}`}
                  >
                    {d.getDate()}
                  </button>
                  <div className="space-y-0.5">
                    {evs.slice(0, 3).map((ev) => (
                      <EventChip key={`${ev.kind}-${ev.id}`} ev={ev} onSelect={setSelected} />
                    ))}
                    {evs.length > 3 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setCursor(new Date(d));
                          setView("day");
                        }}
                        className="text-[11px] text-slate-500 hover:text-slate-800 pl-1 transition-colors"
                        data-testid={`cal-more-${dIso}`}
                      >
                        {t("calendar.more", { n: evs.length - 3 })}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === "week" && (
        <div className="overflow-x-auto -mx-1 px-1">
        <div className="grid grid-cols-7 gap-2 min-w-[560px]" data-testid="cal-week-view">
          {weekDays.map((d, i) => {
            const dIso = isoOf(d);
            const evs = byDate[dIso] || [];
            return (
              <div
                key={dIso}
                data-testid={`cal-week-day-${dIso}`}
                onClick={() => openChooser(dIso)}
                className={`bg-white border rounded-lg min-h-64 p-2 cursor-pointer transition-colors ${dIso === todayIsoStr ? "border-slate-400" : "border-slate-200 hover:border-slate-300"}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    {(WEEKDAYS[L] || WEEKDAYS.en)[i]}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCursor(new Date(d));
                      setView("day");
                    }}
                    className={`text-xs font-semibold h-6 w-6 rounded-full inline-flex items-center justify-center ${dIso === todayIsoStr ? "bg-slate-900 text-white" : "text-slate-600"}`}
                  >
                    {d.getDate()}
                  </button>
                </div>
                <div className="space-y-1">
                  {evs.map((ev) => (
                    <EventChip key={`${ev.kind}-${ev.id}`} ev={ev} onSelect={setSelected} />
                  ))}
                  {evs.length === 0 && <div className="text-[11px] text-slate-300 text-center mt-6">—</div>}
                </div>
              </div>
            );
          })}
        </div>
        </div>
      )}

      {view === "day" && (
        <div className="bg-white border border-slate-200 rounded-lg" data-testid="cal-day-view">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
            <div className="font-serif text-lg text-slate-800" data-testid="cal-day-title">{isoToEu(isoOf(cursor))}</div>
            <Button size="sm" data-testid="cal-day-new-event" onClick={() => openChooser(isoOf(cursor))}
              className="bg-slate-900 hover:bg-slate-800">
              <Plus className="h-4 w-4 mr-1" /> {t("calendar.new_event")}
            </Button>
          </div>
          <div className="divide-y divide-slate-100">
            {(byDate[isoOf(cursor)] || []).length === 0 && (
              <p className="text-sm text-slate-400 px-5 py-6" data-testid="cal-day-empty">{t("calendar.no_events")}</p>
            )}
            {(byDate[isoOf(cursor)] || []).map((ev) => (
              <button
                key={`${ev.kind}-${ev.id}`}
                data-testid={`cal-day-event-${ev.kind}-${ev.id}`}
                onClick={() => setSelected(ev)}
                className="w-full text-left px-4 sm:px-5 py-3 hover:bg-slate-50 transition-colors flex items-start gap-3 sm:gap-4"
              >
                <span className="text-xs font-mono text-slate-500 w-24 shrink-0 pt-0.5">
                  {ev.start_time ? `${ev.start_time}${ev.end_time ? `–${ev.end_time}` : ""}` : t("calendar.all_day")}
                </span>
                <span className={`min-w-0 truncate rounded border px-2 py-1 text-xs font-medium ${eventCls(ev)} ${ev.status === "Completed" ? "opacity-50 line-through" : ""}`}>
                  {ev.description}
                </span>
                <span className="hidden sm:inline text-xs text-slate-500 pt-1 truncate">{ev.matter_name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-md" data-testid="cal-event-popover">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-lg">{selected.description}</DialogTitle>
              </DialogHeader>
              <div className="space-y-2.5 text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-2 py-0.5 rounded-full border text-xs font-semibold ${eventCls(selected)}`} data-testid="cal-event-type">
                    {selected.kind === "task" ? t("calendar.kind_task") : t(`val.${selected.deadline_type}`)}
                  </span>
                  <span className="text-xs text-slate-500">{t(`val.${selected.priority}`)} · {t(`val.${selected.status}`)}</span>
                </div>
                <div className="text-slate-700" data-testid="cal-event-when">
                  {isoToEu(selected.due_date)}
                  {selected.start_time
                    ? ` · ${selected.start_time}${selected.end_time ? `–${selected.end_time}` : ""}`
                    : ` · ${t("calendar.all_day")}`}
                </div>
                <div className="text-slate-600" data-testid="cal-event-matter">
                  {selected.matter_name}{selected.client ? ` · ${selected.client}` : ""}
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button
                  data-testid="cal-open-matter"
                  onClick={() => navigate(`/matters/${selected.matter_id}`)}
                  className="bg-slate-900 hover:bg-slate-800"
                >
                  <ExternalLink className="h-4 w-4 mr-1.5" /> {t("calendar.open_matter")}
                </Button>
                {selected.kind === "deadline" && (
                  <Button
                    variant="outline"
                    data-testid="cal-ics-button"
                    onClick={() => setCalEvent({
                      deadline: selected,
                      matter: {
                        client: selected.client, name: selected.matter_name,
                        court_case_number: selected.court_case_number,
                        court_authority: selected.court_authority,
                      },
                    })}
                  >
                    <CalendarPlus className="h-4 w-4 mr-1.5" /> {t("cal.add")}
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!chooser} onOpenChange={(o) => !o && setChooser(null)}>
        <DialogContent className="max-w-md" data-testid="cal-create-chooser">
          {chooser && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-lg">
                  {t("calendar.create_for", { date: isoToEu(chooser.date) })}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <div className="text-xs font-semibold text-slate-600 mb-1.5">{t("calendar.event_type")}</div>
                  <Select value={chooser.kind} onValueChange={(v) => setChooser((c) => ({ ...c, kind: v }))}>
                    <SelectTrigger data-testid="cal-chooser-kind">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="deadline" data-testid="cal-chooser-kind-deadline">{t("calendar.kind_deadline")}</SelectItem>
                      <SelectItem value="hearing" data-testid="cal-chooser-kind-hearing">{t("calendar.kind_hearing")}</SelectItem>
                      <SelectItem value="task" data-testid="cal-chooser-kind-task">{t("calendar.kind_task")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-600 mb-1.5">{t("calendar.matter")}</div>
                  <Select value={chooser.matterId} onValueChange={(v) => setChooser((c) => ({ ...c, matterId: v }))}>
                    <SelectTrigger data-testid="cal-chooser-matter">
                      <SelectValue placeholder={t("calendar.select_matter")} />
                    </SelectTrigger>
                    <SelectContent>
                      {matters.map((m) => (
                        <SelectItem key={m.id} value={m.id} data-testid={`cal-chooser-matter-${m.id}`}>
                          {m.name}{m.internal_ref ? ` (${m.internal_ref})` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button data-testid="cal-chooser-submit" onClick={confirmChooser}
                  className="w-full bg-slate-900 hover:bg-slate-800">
                  {t("calendar.continue")}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <DeadlineDialog
        open={!!deadlineInit}
        onOpenChange={(o) => !o && setDeadlineInit(null)}
        matterId={createMatter}
        initial={deadlineInit}
        onSaved={() => {
          setDeadlineInit(null);
          load();
          toast.success(t("deadlines.added"));
        }}
      />
      <TaskDialog
        open={!!taskInit}
        onOpenChange={(o) => !o && setTaskInit(null)}
        matterId={createMatter}
        initial={taskInit}
        onSaved={() => {
          setTaskInit(null);
          load();
          toast.success(t("tasks.added"));
        }}
      />
      <CalendarDialog
        open={!!calEvent}
        onOpenChange={(o) => !o && setCalEvent(null)}
        deadline={calEvent?.deadline}
        matter={calEvent?.matter}
      />
    </div>
  );
}
